import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";

import { useFilteredSensors } from "@core/apis/shared/hooks";

import { useGetSensorsDataQuery } from "../customQueryHooks";
import {
  SensorChartConfig,
  SensorEditingCapability,
  SensorTableProps,
  SensorTableRowData,
  UseSensorTableDataReturn,
} from "./types";

const DEFAULT_EDITABLE_SENSOR_TYPES = ["living_air_temperature"];

/**
 * Whether a sensor's type allows metadata editing.
 *
 * Deliberately ignores whether the host app supplied `editing`, so a read-only
 * table still reports the real include/exclude state instead of "N/A".
 */
export const isEditableSensorType = (sensorType: string, editing?: SensorEditingCapability) =>
  (editing?.editableSensorTypes ?? DEFAULT_EDITABLE_SENSOR_TYPES).includes(sensorType);

// Default chart configuration
const DEFAULT_CHART_CONFIG: SensorChartConfig = {
  valueTypeMapping: {
    living_air_temperature: {
      yAxis: 0,
      suffix: "°C",
      title: "Temperature (°C)",
      color: "#1f77b4",
    },
    indoor_relative_humidity: {
      yAxis: 1,
      suffix: "%",
      title: "Humidity (%)",
      color: "#ff7f0e",
    },
    supplytemp: {
      yAxis: 0,
      suffix: "°C",
      title: "Supply Temperature (°C)",
      color: "#2ca02c",
    },
    returntemp: {
      yAxis: 0,
      suffix: "°C",
      title: "Return Temperature (°C)",
      color: "#d62728",
    },
    radiator_system_supply_temp: {
      yAxis: 0,
      suffix: "°C",
      title: "Radiator Supply Temperature (°C)",
      color: "#9467bd",
    },
    water_tank_temperature: {
      yAxis: 0,
      suffix: "°C",
      title: "Water Tank Temperature (°C)",
      color: "#8c564b",
    },
  },
  dateRange: {
    startDate: DateTime.now().minus({ weeks: 2 }).startOf("day"),
    endDate: DateTime.now().endOf("hour"),
  },
  height: 400,
};

// Column factory function
export const createSensorColumns = (
  columnConfig?: SensorTableProps["columnConfig"]
): MRT_ColumnDef<SensorTableRowData>[] => {
  const config = {
    columns: {
      external_id: true,
      sensor_type: true,
      apartment: true,
      floor: true,
      location: false, // Hidden by default in PropAI
      street_address: true,
      ...columnConfig?.columns,
    },
    headers: {
      external_id: "External ID",
      sensor_type: "Sensor Type",
      apartment: "Apartment",
      floor: "Floor",
      location: "Location",
      street_address: "Street Address",
      ...columnConfig?.headers,
    },
    columnSizes: {
      external_id: 150,
      sensor_type: 200,
      apartment: 100,
      floor: 80,
      location: 150,
      street_address: 200,
      ...columnConfig?.columnSizes,
    },
  };

  const columns: MRT_ColumnDef<SensorTableRowData>[] = [];

  if (config.columns.external_id) {
    columns.push({
      accessorKey: "external_id",
      header: config.headers.external_id,
      size: config.columnSizes.external_id,
    });
  }

  if (config.columns.sensor_type) {
    columns.push({
      accessorKey: "sensor_type",
      header: config.headers.sensor_type,
      size: config.columnSizes.sensor_type,
    });
  }

  if (config.columns.apartment) {
    columns.push({
      accessorKey: "apartment",
      header: config.headers.apartment,
      size: config.columnSizes.apartment,
    });
  }

  if (config.columns.floor) {
    columns.push({
      accessorKey: "floor",
      header: config.headers.floor,
      size: config.columnSizes.floor,
    });
  }

  if (config.columns.location) {
    columns.push({
      accessorKey: "location",
      header: config.headers.location,
      size: config.columnSizes.location,
    });
  }

  if (config.columns.street_address) {
    columns.push({
      accessorKey: "street_address",
      header: config.headers.street_address,
      size: config.columnSizes.street_address,
    });
  }

  return columns;
};

// Main hook for sensor table data management
export const useSensorTableData = ({
  substationId,
  sensors: externalSensors,
  isLoading: externalLoading,
  columnConfig,
  selectionConfig,
  chartConfig,
  showChart = true,
  editing,
}: SensorTableProps): UseSensorTableDataReturn => {
  const { t } = useTranslation(["propai_properties"]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // Merge chart config with defaults
  const mergedChartConfig = useMemo(
    () => ({
      ...DEFAULT_CHART_CONFIG,
      ...chartConfig,
      valueTypeMapping: {
        ...DEFAULT_CHART_CONFIG.valueTypeMapping,
        ...chartConfig?.valueTypeMapping,
      },
    }),
    [chartConfig]
  );

  // Use external sensors if provided, otherwise fetch all sensors
  const {
    data: fetchedSensors,
    isLoading: fetchingLoading,
    error: fetchingError,
    isError: hasFetchingError,
  } = useFilteredSensors(
    substationId,
    [], // Empty array to get all sensors
    {
      enabled:
        (!externalSensors || externalSensors.length === 0) &&
        (typeof window === "undefined" || window.location.port !== "3000"), // Disable completely for Platform (port 3000)
    }
  );

  // Throw error immediately if fetching fails
  if (hasFetchingError && fetchingError) {
    throw new Error(`Failed to fetch sensors: ${fetchingError.message || "Unknown error"}`);
  }

  // Determine data source and loading state
  // fetchedSensors is now properly flattened from FlexStations to Sensor[]
  const sensors = externalSensors || (fetchedSensors ?? []);
  // Only show loading if we don't have sensors yet
  const isLoading = sensors.length === 0 && (externalLoading || fetchingLoading);

  // Transform sensor data for table
  const tableData = useMemo(() => {
    if (!sensors || !Array.isArray(sensors)) {
      return [];
    }

    // Ensure we have Sensor objects with the expected properties
    const filteredSensors = sensors.filter((sensor: any) => {
      return sensor && sensor.uid && sensor.external_id;
    });

    return filteredSensors.map((sensor: any) => {
      const sensorType = String(
        sensor.sensor_type_technical_name || sensor.sensor_type || "Unknown"
      );
      const included = sensor.enabled !== false;

      return {
        uid: String(sensor.uid), // Ensure UID is string
        external_id: String(sensor.external_id || "N/A"),
        sensor_type: sensorType,
        apartment: String(sensor.apartment || "N/A"),
        floor: String(sensor.floor || "N/A"),
        location: String(sensor.location || "N/A"),
        street_address: String(sensor.street_address || "N/A"),
        enabled: included,
        average_calculations: isEditableSensorType(sensorType, editing)
          ? included
            ? t("included", { ns: "propai_properties" })
            : t("excluded", { ns: "propai_properties" })
          : "N/A",
      };
    });
  }, [sensors, t, editing]);

  // Create column definitions
  const columns = useMemo(() => createSensorColumns(columnConfig), [columnConfig]);

  // Handle selection based on configuration
  const selectedSensorUids = useMemo(() => {
    if (!sensors || !rowSelection) return [];

    if (selectionConfig?.mode === "uid") {
      // Direct UID-based selection (PropAI style)
      return Object.keys(rowSelection).filter((uid) => rowSelection[uid]);
    } else {
      // Row index-based selection (Platform-Web style)
      return Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((index) => sensors[parseInt(index)]?.uid)
        .filter(Boolean);
    }
  }, [rowSelection, sensors, selectionConfig?.mode]);

  // Fetch chart data for selected sensors
  const sensorDataQuery: {
    data: Record<string, any[]> | undefined;
    isSuccess: boolean;
    isLoading: boolean;
    isError: boolean;
  } = useGetSensorsDataQuery(substationId, selectedSensorUids, mergedChartConfig.dateRange, {
    enabled: showChart && selectedSensorUids.length > 0,
  });

  // Prepare chart series data
  const chartSeries = useMemo(() => {
    // useGetSensorsDataQuery returns a combined result with data and isSuccess
    if (
      !sensorDataQuery.isSuccess ||
      !sensorDataQuery.data ||
      selectedSensorUids.length === 0 ||
      !showChart
    ) {
      return [];
    }

    const series: any[] = [];

    selectedSensorUids.forEach((sensorUid) => {
      const sensor = sensors?.find((s) => s.uid === sensorUid);
      const data = sensorDataQuery.data[sensorUid] || [];

      // Get all unique value keys from sensor data
      const valueKeys = new Set<string>();
      data.forEach((reading: any) => {
        if (reading.values) {
          Object.keys(reading.values).forEach((key) => valueKeys.add(key));
        }
      });

      // Create series for each value type
      valueKeys.forEach((valueKey) => {
        const isKnownValue = valueKey in mergedChartConfig.valueTypeMapping;
        const config = mergedChartConfig.valueTypeMapping[valueKey];

        series.push({
          name: `${sensor?.external_id || sensorUid} - ${valueKey}`,
          type: "spline",
          yAxis: isKnownValue ? config.yAxis : 1,
          data: data
            .map((reading: any) => [
              DateTime.fromISO(reading.timestamp).toMillis(),
              reading.values?.[valueKey] || null,
            ])
            .filter(([, value]) => value !== null),
          tooltip: {
            valueSuffix: isKnownValue ? config.suffix : "",
            valueDecimals: 1,
          },
          color: isKnownValue ? config.color : undefined,
        });
      });
    });

    return series;
  }, [
    sensorDataQuery.isSuccess,
    sensorDataQuery.data,
    selectedSensorUids,
    sensors,
    mergedChartConfig.valueTypeMapping,
    showChart,
  ]);

  // Custom selection handler that respects selection mode
  const handleSelectionChange = useMemo(() => {
    if (selectionConfig?.onSelectionChange) {
      return (
        selection: MRT_RowSelectionState | ((prev: MRT_RowSelectionState) => MRT_RowSelectionState)
      ) => {
        setRowSelection((prev) => {
          const next = typeof selection === "function" ? selection(prev) : selection;

          // Calculate selected UIDs based on mode
          let uids: string[] = [];
          if (selectionConfig.mode === "uid") {
            uids = Object.keys(next).filter((uid) => next[uid]);
          } else {
            uids = Object.keys(next)
              .filter((key) => next[key])
              .map((index) => sensors?.[parseInt(index)]?.uid)
              .filter(Boolean);
          }

          selectionConfig.onSelectionChange?.(uids);
          return next;
        });
      };
    }
    return setRowSelection;
  }, [selectionConfig, sensors]);

  return {
    // Table data
    tableData,
    sensors,
    isLoading,

    // Selection state
    rowSelection,
    setRowSelection: handleSelectionChange,
    selectedSensorUids,

    // Chart data
    chartSeries,
    hasChartData: sensorDataQuery.isSuccess && sensorDataQuery.data && chartSeries.length > 0,

    // Column definitions
    columns,

    // Error handling
    error: hasFetchingError ? fetchingError : null,
  };
};
